"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SupportedCurrency } from "@/lib/format";
import { minNightsForCheckin, type MinStayRange } from "@/lib/min-stay";

/** yyyy-mm-dd in LOCAL time (the calendar grid builds local Dates). */
const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface Props {
  value: DateRange;
  onChange: (r: DateRange) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  /** ISO yyyy-mm-dd dates that should be disabled (booked / unavailable) */
  blockedDates?: string[];
  /** Optional per-night prices keyed by yyyy-mm-dd. When passed, the picker
   *  renders the price under each available day (Airbnb-style). */
  dailyPrices?: Record<string, number>;
  /** Currency for `dailyPrices` (defaults to EGP). */
  currency?: SupportedCurrency;
  /** Minimum nights between check-in and check-out. Used for monthly-rental
   *  units (typically 28). When set, checkout dates that violate the minimum
   *  are greyed out + rejected by the click handler. */
  minNights?: number | null;
  /** Per-date minimum-stay ranges. When present the picker resolves the
   *  minimum against the check-in the guest is actually choosing, instead of
   *  applying one season-peak number to every date (L-054). */
  minStayRanges?: MinStayRange[];
  locale?: "en" | "ar";
  /** Optional "skip dates" action shown in the footer. Used by the mobile
   *  search funnel so a visitor can search without picking dates from the date
   *  screen itself. Other callers (booking) omit it → no skip button. */
  onSkip?: () => void;
  skipLabel?: string;
}

const dateToIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** True if any NIGHT in [start, end) is booked. Mirror of the in-component
 *  rangeOverlapsBlocked, but pure so the Month renderer can use it too. */
function rangeHasBlockedNight(start: Date, end: Date, blockedSet: Set<string> | null): boolean {
  if (!blockedSet || blockedSet.size === 0) return false;
  for (let t = +start; t < +end; t += 86_400_000) {
    if (blockedSet.has(dateToIso(new Date(t)))) return true;
  }
  return false;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function DatePicker({ value, onChange, onClose, anchorRef, blockedDates, dailyPrices, currency = "EGP", minNights, minStayRanges, locale = "en", onSkip, skipLabel }: Props) {
  const dl = locale === "ar"
    ? { pickIn: "اختر تاريخ الوصول", pickOut: "اختر تاريخ المغادرة", nights: (n: number) => `${n} ليلة` }
    : { pickIn: "Pick a check-in date", pickOut: "pick check-out", nights: (n: number) => `${n} night${n > 1 ? "s" : ""}` };
  // Open on the month of the already-chosen check-in, else the current month.
  // (Was hardcoded to July 2026 — a dev leftover that opened every picker on
  // the wrong month.)
  const [view, setView] = useState(() => {
    const base = value.start ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // On phones we render as a bottom sheet instead of an anchored popover —
  // anchored popovers below a field get clipped by the viewport on small
  // screens. Match Tailwind's md breakpoint (768px).
  const [isMobile, setIsMobile] = useState(false);
  // True right after a check-in tap auto-filled the checkout at the minimum
  // stay: the calendar stays open and the NEXT later tap moves the checkout
  // (instead of starting a new stay). Cleared by an explicit checkout tap.
  const [autoEnd, setAutoEnd] = useState(false);

  // Build a Set once per render for O(1) lookup while drawing each day cell.
  const blockedSet = blockedDates ? new Set(blockedDates) : null;

  // Track viewport width — switch between sheet and popover on resize.
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Lock body scroll while the mobile sheet is open
  useEffect(() => {
    if (!isMobile) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = orig;
    };
  }, [isMobile]);

  /** Does the inclusive range [a, b) overlap any blocked date? */
  const rangeOverlapsBlocked = (a: Date, b: Date): boolean => {
    if (!blockedSet || blockedSet.size === 0) return false;
    for (let d = +a; d < +b; d += 86_400_000) {
      if (blockedSet.has(dateToIso(new Date(d)))) return true;
    }
    return false;
  };

  // Position the popover anchored to the field.
  // The popover is position: fixed, so coordinates are viewport-relative —
  // do NOT add window.scrollY (that's for absolute positioning).
  useEffect(() => {
    if (!anchorRef.current) return;
    const place = () => {
      const r = anchorRef.current!.getBoundingClientRect();
      const width = Math.min(580, window.innerWidth - 32);
      setPos({
        top: r.bottom + 8,
        left: Math.max(16, Math.min(r.left, window.innerWidth - width - 16)),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, { passive: true });
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  function pick(d: Date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return;

    const isBlockedNight = !!blockedSet?.has(dateToIso(d));
    const choosingCheckout = !!value.start && (!value.end || autoEnd) && d > value.start;

    if (choosingCheckout) {
      // Back-to-back: the checkout day MAY itself be booked (the next guest's
      // check-in) — we only require every NIGHT in [start, d) to be free.
      if (rangeOverlapsBlocked(value.start!, d)) {
        // The span crosses an occupied night → restart from d if d is a free
        // night, otherwise ignore the tap.
        if (!isBlockedNight) onChange({ start: d, end: null });
        return;
      }
      const nights = Math.round((+d - +value.start!) / 86_400_000);
      // Minimum for the START the guest picked — not the season peak.
      const minForStart = minNightsForCheckin(toISO(value.start!), minStayRanges, minNights);
      if (nights < Math.max(1, minForStart)) return;
      setAutoEnd(false);
      onChange({ start: value.start!, end: d });
      // Both dates picked — auto-close after a short delay so the user sees the
      // range highlight before the popover dismisses.
      setTimeout(onClose, 250);
      return;
    }

    // (Re)choosing a check-in — must be a FREE night.
    if (isBlockedNight) return;
    // Minimum stay above one night: pre-select the shortest valid stay
    // (check-in + minimum) and KEEP the calendar open, so the guest sees dates
    // and price at once and can still tap a later day to extend (Maged,
    // 2026-09-29 — Mivida's 180-night minimum made guests scroll six months to
    // find a checkout). Skipped when that span would cross a booked night: the
    // guest then picks the checkout by hand, exactly as before.
    const minForStart = minNightsForCheckin(toISO(d), minStayRanges, minNights);
    if (minForStart > 1) {
      const end = new Date(d);
      end.setDate(end.getDate() + minForStart);
      if (!rangeOverlapsBlocked(d, end)) {
        setAutoEnd(true);
        onChange({ start: d, end });
        return;
      }
    }
    setAutoEnd(false);
    onChange({ start: d, end: null });
  }

  function summary() {
    const localeCode = locale === "ar" ? "ar-EG" : "en-US";
    if (value.start && value.end) {
      const nights = Math.round((+value.end - +value.start) / 86_400_000);
      const fmt = (d: Date) =>
        d.toLocaleDateString(localeCode, { month: "short", day: "numeric" });
      const extend = autoEnd
        ? (locale === "ar" ? " · الحد الأدنى للإقامة — اختر تاريخاً لاحقاً للتمديد" : " · minimum stay — tap a later date to extend")
        : "";
      return `${fmt(value.start)} – ${fmt(value.end)} · ${dl.nights(nights)}${extend}`;
    }
    if (value.start) {
      const hint =
        minNights && minNights > 0
          ? ` · min ${dl.nights(minNights)}`
          : "";
      return `Check-in ${value.start.toLocaleDateString(localeCode, { month: "short", day: "numeric" })} · ${dl.pickOut}${hint}`;
    }
    if (minNights && minNights > 0) {
      return `${dl.pickIn} · ${minNights}-night minimum stay`;
    }
    return dl.pickIn;
  }

  if (typeof document === "undefined") return null;
  // Desktop needs a positioned popover; mobile renders as a sheet regardless
  // of where the anchor sits.
  if (!isMobile && !pos) return null;

  const sheetMode = isMobile;

  return createPortal(
    <>
      {sheetMode && (
        <div
          className="fixed inset-0 z-[219] bg-black/40 backdrop-blur-sm fade-up"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
    <div
      ref={popRef}
      className={
        sheetMode
          ? "fixed inset-x-0 bottom-0 max-h-[88dvh] overflow-y-auto bg-white border-t border-ss-line rounded-t-[22px] shadow-[var(--shadow-ss-xl)] z-[220] p-5 pb-[max(20px,env(safe-area-inset-bottom))] fade-up"
          : "fixed bg-white border border-ss-line rounded-[22px] shadow-[var(--shadow-ss-xl)] z-[220] p-5 fade-up"
      }
      style={
        sheetMode
          ? undefined
          : { top: pos!.top, left: pos!.left, minWidth: 580, maxWidth: "calc(100vw - 32px)" }
      }
    >
      <div className="flex justify-between items-center mb-3.5">
        <button
          type="button"
          onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
          className="w-8 h-8 rounded-full hover:bg-ss-bg-soft text-lg"
          aria-label="Previous month"
        >
          ‹
        </button>
        <strong className="text-sm">Select dates</strong>
        <button
          type="button"
          onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
          className="w-8 h-8 rounded-full hover:bg-ss-bg-soft text-lg"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Month
          month={view}
          value={value}
          onPick={pick}
          blockedSet={blockedSet}
          dailyPrices={dailyPrices}
          currency={currency}
          minNights={minNights}
          locale={locale}
          choosingEnd={!!value.start && (!value.end || autoEnd)}
        />
        <Month
          month={new Date(view.getFullYear(), view.getMonth() + 1, 1)}
          value={value}
          onPick={pick}
          blockedSet={blockedSet}
          dailyPrices={dailyPrices}
          currency={currency}
          minNights={minNights}
          locale={locale}
          choosingEnd={!!value.start && (!value.end || autoEnd)}
        />
      </div>

      {blockedSet && blockedSet.size > 0 && (
        <div className="mt-3 flex items-center gap-2 text-[11.5px] text-ss-muted">
          <span
            className="inline-grid place-items-center w-5 h-5 rounded-full bg-[#f4f5f7] border border-[#e3e8ef] text-ss-line text-[11px] line-through"
            aria-hidden="true"
          >
            14
          </span>
          Crossed-out dates are already booked
        </div>
      )}

      <div className="flex justify-between items-center mt-4 pt-3.5 border-t border-ss-line">
        <span className="text-[12.5px] text-ss-muted">{summary()}</span>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3.5 py-2 rounded-full border border-ss-line text-xs font-semibold"
            onClick={() => onChange({ start: null, end: null })}
          >
            Clear
          </button>
          <button
            type="button"
            className="px-3.5 py-2 rounded-full bg-ss-blue text-white text-xs font-semibold hover:bg-ss-blue-dark"
            onClick={onClose}
          >
            Apply
          </button>
        </div>
      </div>

      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className="w-full mt-3 text-ss-blue text-sm font-semibold py-1.5"
        >
          {skipLabel ?? "Search without dates"}
        </button>
      )}
    </div>
    </>,
    document.body
  );
}

/** Compact price label for a calendar cell. EGP 5,816 → "5.8k". USD 635 → "$635".
 *  Keeps the cell readable on mobile + dense desktop grids. */
function compactPrice(amount: number, currency: SupportedCurrency): string {
  if (currency === "USD") {
    if (amount >= 1000) return `$${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
    return `$${amount}`;
  }
  // EGP — values regularly 5k-50k, sometimes 100k+. Always abbreviate.
  if (amount >= 1000) return `${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
  return String(amount);
}

function Month({
  month,
  value,
  onPick,
  blockedSet,
  dailyPrices,
  currency,
  minNights,
  locale = "en",
  choosingEnd,
}: {
  month: Date;
  value: DateRange;
  onPick: (d: Date) => void;
  blockedSet: Set<string> | null;
  dailyPrices?: Record<string, number>;
  currency: SupportedCurrency;
  minNights?: number | null;
  locale?: "en" | "ar";
  /** Checkout mode: no end yet, OR an auto-filled minimum-stay end the next tap may move. */
  choosingEnd?: boolean;
}) {
  const y = month.getFullYear();
  const mo = month.getMonth();
  const first = new Date(y, mo, 1);
  const last = new Date(y, mo + 1, 0);
  const startDay = (first.getDay() + 6) % 7;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Whether ANY cell in this month has a price — if so, every cell shifts to
  // the taller layout so heights line up (avoid jagged grid).
  const hasAnyPrice =
    dailyPrices &&
    Object.keys(dailyPrices).some((iso) => iso.startsWith(`${y}-${String(mo + 1).padStart(2, "0")}-`));

  const cells: React.ReactNode[] = [];
  for (let i = 0; i < startDay; i++) {
    cells.push(<span key={`b${i}`} className={hasAnyPrice ? "min-h-[52px]" : "invisible"} />);
  }
  for (let d = 1; d <= last.getDate(); d++) {
    const date = new Date(y, mo, d);
    const iso = `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const past = date < today;
    const blocked = !!blockedSet?.has(iso);
    const violatesMin =
      !!minNights &&
      minNights > 0 &&
      !!value.start &&
      !!choosingEnd &&
      date > value.start &&
      Math.round((+date - +value.start) / 86_400_000) < minNights;

    // Back-to-back: while picking the checkout (start chosen, no end yet), a
    // day is a valid checkout iff every NIGHT in [start, date) is free. The
    // checkout day itself MAY be booked ("checkout-only" turnover day).
    const choosingCheckout = !!value.start && !!choosingEnd && date > value.start;
    let validCheckout = false;
    let checkoutOnly = false; // a booked day that is nonetheless a valid checkout
    if (choosingCheckout && value.start) {
      const crosses = rangeHasBlockedNight(value.start, date, blockedSet);
      const nights = Math.round((+date - +value.start) / 86_400_000);
      validCheckout = !crosses && nights >= Math.max(1, minNights ?? 0);
      checkoutOnly = validCheckout && blocked;
    }

    const isStart = value.start && +date === +value.start;
    const isEnd = value.end && +date === +value.end;
    const between =
      value.start && value.end && date > value.start && date < value.end;

    // Disabled: in checkout mode, enable ONLY valid checkout targets (this both
    // turns the first booked day after check-in into a clickable checkout AND
    // disables every day past it). Otherwise (choosing a check-in), booked or
    // below-min days stay disabled as before.
    const disabled = past || (choosingCheckout ? !validCheckout : blocked || violatesMin);
    // Suppress the per-night price on a checkout-only (booked turnover) day —
    // that night is never billed (pricing iterates [start, end)), so showing a
    // price there would mislead. Lets the "checkout only" aria-label apply too.
    const price = !disabled && !checkoutOnly ? dailyPrices?.[iso] : undefined;

    let cls = hasAnyPrice
      ? "min-h-[52px] flex flex-col items-center justify-center rounded-[10px] text-sm font-medium transition-colors px-0.5"
      : "aspect-square grid place-items-center rounded-full text-sm font-medium transition-colors";
    if (past) cls += " text-ss-line line-through cursor-not-allowed";
    else if (isStart || isEnd) cls += " bg-ss-text text-white font-bold cursor-pointer";
    else if (between) cls += " bg-ss-blue-50 text-ss-blue rounded-none cursor-pointer";
    else if (choosingCheckout && validCheckout)
      // selectable checkout target. A "checkout-only" (booked) day gets a
      // distinct muted-but-clickable look; a free valid day just hovers.
      cls += checkoutOnly
        ? " bg-[#eef2f7] text-ss-text cursor-pointer ring-1 ring-inset ring-[#d7deea]"
        : " hover:bg-ss-bg-soft cursor-pointer";
    else if (blocked) cls += " text-ss-line line-through bg-[#f4f5f7] cursor-not-allowed";
    else if (violatesMin) cls += " text-ss-line bg-[#f7f8fa] cursor-not-allowed";
    else cls += " hover:bg-ss-bg-soft cursor-pointer";
    if (isStart && !isEnd) cls += " rounded-r-none";
    if (isEnd && !isStart) cls += " rounded-l-none";

    const ariaLabel = price
      ? `${iso} — ${currency} ${price}`
      : checkoutOnly
        ? `${iso} — checkout only`
        : blocked
          ? `${iso} — booked`
          : iso;

    cells.push(
      <button
        type="button"
        key={d}
        onClick={() => !disabled && onPick(date)}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cls}
      >
        <span>{d}</span>
        {price !== undefined && (
          <span
            className={`mt-0.5 text-[10px] leading-none font-normal ${
              isStart || isEnd ? "text-white/85" : between ? "text-ss-blue" : "text-ss-muted"
            }`}
          >
            {compactPrice(price, currency)}
          </span>
        )}
      </button>
    );
  }

  return (
    <div>
      <div className="text-center font-bold text-sm mb-2">
        {locale === "ar"
          ? new Date(y, mo, 1).toLocaleDateString("ar-EG", { month: "long", year: "numeric" })
          : `${MONTHS[mo]} ${y}`}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-[10.5px] font-bold text-ss-muted uppercase tracking-wider mb-1">
        {(locale === "ar" ? ["ن", "ث", "ر", "خ", "ج", "س", "ح"] : ["M", "T", "W", "T", "F", "S", "S"]).map((d, i) => (
          <span key={i} className="text-center py-1.5">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">{cells}</div>
    </div>
  );
}
